import { array, fn } from '@ember/helper';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { PopupListItem, Service } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';

import joinClasses from 'webapp/helpers/join-classes';
import visibleCheckItem from 'webapp/modifiers/visible-check-item';
import AvatarOrInitialsComponent from 'webapp/components/avatar-or-initials';
import ClientSubscriptionStatusComponent from 'webapp/components/client/subscription-status';
import DummyCellComponent from 'webapp/components/dummy-cell';
import EditableModelPropertyComponent from 'webapp/components/editable-model-property';
import FlexibleToggleCellComponent from 'webapp/components/flexible-toggle-cell';
import FormCheckboxComponent from 'webapp/components/form/checkbox';
import OptionsButtonComponent from 'webapp/components/options-button';
import OwnerSelectComponent from 'webapp/components/owner-select';
import TagContainerComponent from 'webapp/components/tag-container';
import ToolTipWithIconComponent from 'webapp/components/tool-tip-with-icon';

import type BatchSelect from 'webapp/models-custom/batch-select';
import type Space from 'webapp/models-custom/space';

export interface Args {
	model: Space;
	batchSelect: BatchSelect<Space>;
	onDelete: (model: Space) => void;
}

type Action = 'loginAs' | 'deleteClient' | 'openAccountApp';
type Option = PopupListItem<Action>;

export default class ClientsTableRowComponent extends Component<Args> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('popup') declare popupService: Service.Popup;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('account') declare accountService: Service.Account;

	@tracked isVisibleOnScreen = false;
	@tracked showAll = false;

	get options(): Option[] {
		let options: Option[] = [];

		options = [
			{
				icon: 'UserAccess',
				text: 'Switch account',
				action: 'loginAs',
			},
		];

		if (this.currentUserService.isAdmin && !this.hasPartnerResellerId) {
			options.push({
				icon: 'Pencil',
				text: 'Edit',
				action: 'openAccountApp',
			});

			options.push({
				class: 'delete',
				text: 'Delete',
				action: 'deleteClient',
				icon: 'Trash',
			});
		}

		return options;
	}

	get datasourcesTooltip(): string {
		const sources: string[] = [];
		(this.args.model.datasources || []).forEach((source) => {
			if (source.count) {
				sources.push(source.count + ' ' + source.name);
			}
		});
		return sources.join('<br />');
	}

	get datasetsTooltip(): string {
		const datasets: string[] = [];
		(this.args.model.datasets || []).forEach((dataset) => {
			if (dataset.count) {
				datasets.push(dataset.count + ' ' + dataset.name);
			}
		});
		return datasets.join('<br />');
	}

	get isSelected(): boolean {
		const { batchSelect, model } = this.args;
		return batchSelect.isItemSelected(model);
	}

	get hasPartnerResellerId() {
		return this.args.model.parentResellerId !== null && this.args.model.parentResellerId > 0;
	}

	@action
	doOptionsAction(actionName: string): void {
		this[actionName as Action]();
	}

	@action
	loginAs() {
		this.analyticsService.event(TrackingEvent.SWITCH_ACCOUNT, { origin: 'list' });

		window.location.replace(this.args.model.loginUrl);
	}

	@action
	deleteClient() {
		if (this.currentUserService.handleDemo()) {
			return;
		}

		this.args.batchSelect.unselectAll();

		const affirmativeFunc = () => {
			this.args.onDelete(this.args.model);
		};
		this.popupService.openConfirmPopup({
			onConfirm: affirmativeFunc,
			title: `Do you want to delete the account "${this.args.model.companyName}"?`,
			text: 'By deleting this account, its data sources will be disconnected and data will no longer be fetched. This action cannot be undone, and all data stored in our system will be permanently deleted.',
			confirmAction: 'Delete',
			cancelAction: 'Cancel',
		});
	}

	@action
	openAccountApp() {
		const numericId = this.args.model.numericId;
		if (numericId) {
			this.accountService.openAccountAppClientManagerEditGeneral(numericId);
		}
	}

	@action
	toggleShowAll() {
		this.showAll = !this.showAll;
	}

	@action
	visibleChanged(isVisible: boolean): void {
		this.isVisibleOnScreen = isVisible;
	}
	<template>
		{{#if this.isVisibleOnScreen}}
			<tr
				class={{joinClasses
					(array
						"table-row" (if this.showAll "all-shown") (if this.isSelected "selected")
					)
				}}
				data-id={{@model.id}}
			>
				<td class="cc first">
					<FormCheckboxComponent
						@name="clients-table-row"
						@selected={{this.isSelected}}
						@theme="blue"
						@onChange={{fn @batchSelect.changeSelection @model}}
					/>
				</td>

				<td class="c0 no-grow">
					<AvatarOrInitialsComponent
						@name={{@model.companyName}}
						@color={{@model.color}}
					/>
				</td>

				<td class="c1">

					<div class="flex">
						<EditableModelPropertyComponent
							@model={{@model}}
							@editableProperty="companyName"
						/>

						<div class="labelicons">
							{{#if @model.model.newBadge}}
								<div class="emblem emblem--new"></div>
							{{/if}}
						</div>
					</div>
				</td>

				<td class="prio-medium" data-label="Account manager: ">
					<OwnerSelectComponent
						@model={{@model}}
						@canEdit={{false}}
						@isOwnerNameHidden={{true}}
					/>
				</td>

				<td class="prio-low" data-label="Users: ">
					{{@model.userCount}}
				</td>

				<td class="prio-low" data-label="Data sources: ">
					<div class="sources">
						{{@model.sourceCount}}
						{{#if this.datasourcesTooltip}}
							<ToolTipWithIconComponent @text={{this.datasourcesTooltip}} />
						{{/if}}
					</div>
				</td>

				<td class="prio-low" data-label="Datasets: ">
					<div class="sources">
						{{@model.datasetCount}}
						{{#if this.datasetsTooltip}}
							<ToolTipWithIconComponent @text={{this.datasetsTooltip}} />
						{{/if}}
					</div>
				</td>

				<td class="prio-low" data-label="Dashboards: ">
					{{@model.boardCount}}
				</td>

				<td class="c8" data-label="Subscription: ">
					<ClientSubscriptionStatusComponent @model={{@model}} />
				</td>

				<td class="c9 prio-low" data-label="Tags: ">
					<div>
						<TagContainerComponent
							@model={{@model}}
							@canEdit={{@model.currentUser.isAdmin}}
							@showSingleTagName={{true}}
							@maxShownTags={{3}}
						/>
					</div>
				</td>

				<td class="c10 button-ct last">
					<OptionsButtonComponent
						@options={{this.options}}
						@onAction={{this.doOptionsAction}}
					/>
				</td>

				<FlexibleToggleCellComponent
					@showAll={{this.showAll}}
					@toggleShowAll={{this.toggleShowAll}}
				/>
			</tr>

		{{else}}

			<tr class="table-row" data-id={{@model.id}} {{visibleCheckItem this.visibleChanged}}>
				<DummyCellComponent />
			</tr>

		{{/if}}
	</template>
}
